import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BusinessVerificationPanel = ({ onSubmit, onPrevious, isLoading, agentData }) => {
  const [formData, setFormData] = useState({
    businessRegistrationNumber: agentData?.businessRegistrationNumber || '',
    taxId: agentData?.taxId || '',
    businessLicense: agentData?.businessLicense || '',
    operatingLicense: agentData?.operatingLicense || '',
    bankName: agentData?.bankName || '',
    accountNumber: agentData?.accountNumber || '',
    routingNumber: agentData?.routingNumber || '',
    accountType: agentData?.accountType || '',
    accountHolderName: agentData?.accountHolderName || '',
    swiftCode: agentData?.swiftCode || '',
    documents: agentData?.documents || {
      businessRegistration: null,
      taxCertificate: null,
      bankStatement: null,
      operatingLicense: null,
      identificationDoc: null
    }
  });

  const [errors, setErrors] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});

  const accountTypeOptions = [
    { value: 'checking', label: 'Checking Account' },
    { value: 'savings', label: 'Savings Account' },
    { value: 'business', label: 'Business Account' }
  ];

  const requiredDocuments = [
    {
      key: 'businessRegistration',
      name: 'Business Registration',
      description: 'Certificate of incorporation or business registration',
      required: agentData?.agentType === 'business' || agentData?.agentType === 'franchise'
    },
    {
      key: 'taxCertificate',
      name: 'Tax Certificate',
      description: 'Tax identification certificate or EIN document',
      required: true
    },
    {
      key: 'bankStatement',
      name: 'Bank Statement',
      description: 'Recent bank statement (last 3 months)',
      required: true
    },
    {
      key: 'operatingLicense',
      name: 'Operating License',
      description: 'Money service business license (if applicable)',
      required: agentData?.agentType === 'business' || agentData?.agentType === 'franchise'
    },
    {
      key: 'identificationDoc',
      name: 'Government ID',
      description: 'Valid government-issued photo identification',
      required: true
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    setUploadStatus(prev => ({ ...prev, [documentType]: 'uploading' }));

    try {
      // Simulate file upload
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockUrl = `https://example.com/documents/${documentType}_${Date.now()}.pdf`;
      
      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev?.documents,
          [documentType]: {
            file: file,
            url: mockUrl,
            uploadedAt: new Date()?.toISOString()
          }
        }
      }));

      setUploadStatus(prev => ({ ...prev, [documentType]: 'success' }));
      
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus(prev => ({ ...prev, [documentType]: 'error' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Banking information validation
    if (!formData?.bankName?.trim()) newErrors.bankName = 'Bank name is required';
    if (!formData?.accountNumber?.trim()) newErrors.accountNumber = 'Account number is required';
    if (!formData?.routingNumber?.trim()) newErrors.routingNumber = 'Routing number is required';
    if (!formData?.accountType) newErrors.accountType = 'Account type is required';
    if (!formData?.accountHolderName?.trim()) newErrors.accountHolderName = 'Account holder name is required';

    // Business-specific validation
    if (agentData?.agentType === 'business' || agentData?.agentType === 'franchise') {
      if (!formData?.businessRegistrationNumber?.trim()) {
        newErrors.businessRegistrationNumber = 'Business registration number is required';
      }
      if (!formData?.businessLicense?.trim()) {
        newErrors.businessLicense = 'Business license number is required';
      }
    }

    if (!formData?.taxId?.trim()) newErrors.taxId = 'Tax ID is required';

    // Document validation
    const requiredDocs = requiredDocuments?.filter(doc => doc?.required);
    requiredDocs?.forEach(doc => {
      if (!formData?.documents?.[doc?.key]) {
        newErrors[`document_${doc?.key}`] = `${doc?.name} is required`;
      }
    });

    // Account number validation (basic)
    if (formData?.accountNumber && formData?.accountNumber?.length < 8) {
      newErrors.accountNumber = 'Account number must be at least 8 digits';
    }

    // Routing number validation (US format)
    if (formData?.routingNumber && !/^\d{9}$/?.test(formData?.routingNumber)) {
      newErrors.routingNumber = 'Routing number must be 9 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const getUploadButtonText = (status) => {
    switch (status) {
      case 'uploading': return 'Uploading...';
      case 'success': return 'Uploaded';
      case 'error': return 'Retry Upload';
      default: return 'Upload Document';
    }
  };

  const getUploadButtonIcon = (status) => {
    switch (status) {
      case 'uploading': return 'Loader';
      case 'success': return 'Check';
      case 'error': return 'AlertCircle';
      default: return 'Upload';
    }
  };

  const isBusinessAgent = agentData?.agentType === 'business' || agentData?.agentType === 'franchise';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center space-y-2 mb-6">
        <h2 className="text-2xl font-semibold text-foreground">Business Verification</h2>
        <p className="text-muted-foreground">
          Provide your business documentation and banking information for commission payments.
        </p>
      </div>

      {/* Business Registration Info */}
      {isBusinessAgent && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Building2" size={20} />
            <span>Business Registration Details</span>
          </h3>

          <Input
            label="Business Registration Number"
            type="text"
            placeholder="Enter business registration number"
            value={formData?.businessRegistrationNumber}
            onChange={(e) => handleInputChange('businessRegistrationNumber', e?.target?.value)}
            error={errors?.businessRegistrationNumber}
            description="Official business registration or incorporation number"
            required
          />

          <Input
            label="Business License Number"
            type="text"
            placeholder="Enter business license number"
            value={formData?.businessLicense}
            onChange={(e) => handleInputChange('businessLicense', e?.target?.value)}
            error={errors?.businessLicense}
            description="Money service business license or relevant operating license"
            required
          />

          <Input
            label="Operating License (if applicable)"
            type="text"
            placeholder="Enter operating license number"
            value={formData?.operatingLicense}
            onChange={(e) => handleInputChange('operatingLicense', e?.target?.value)}
            description="Additional operating licenses for money service businesses"
          />
        </div>
      )}

      {/* Tax Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="FileText" size={20} />
          <span>Tax Identification</span>
        </h3>

        <Input
          label="Tax ID / EIN"
          type="text"
          placeholder="Enter tax identification number"
          value={formData?.taxId}
          onChange={(e) => handleInputChange('taxId', e?.target?.value)}
          error={errors?.taxId}
          description="Social Security Number (SSN) or Employer Identification Number (EIN)"
          required
        />
      </div>

      {/* Banking Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="CreditCard" size={20} />
          <span>Commission Payout Banking Information</span>
        </h3>

        <Input
          label="Bank Name"
          type="text"
          placeholder="Enter bank name"
          value={formData?.bankName}
          onChange={(e) => handleInputChange('bankName', e?.target?.value)}
          error={errors?.bankName}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Account Number"
            type="text"
            placeholder="Enter account number"
            value={formData?.accountNumber}
            onChange={(e) => handleInputChange('accountNumber', e?.target?.value)}
            error={errors?.accountNumber}
            required
          />

          <Input
            label="Routing Number"
            type="text"
            placeholder="Enter routing number"
            value={formData?.routingNumber}
            onChange={(e) => handleInputChange('routingNumber', e?.target?.value)}
            error={errors?.routingNumber}
            description="9-digit routing number"
            required
          />
        </div>

        <Select
          label="Account Type"
          placeholder="Select account type"
          options={accountTypeOptions}
          value={formData?.accountType}
          onChange={(value) => handleInputChange('accountType', value)}
          error={errors?.accountType}
          required
        />

        <Input
          label="Account Holder Name"
          type="text"
          placeholder="Enter account holder name"
          value={formData?.accountHolderName}
          onChange={(e) => handleInputChange('accountHolderName', e?.target?.value)}
          error={errors?.accountHolderName}
          description="Name on the bank account (must match business/personal name)"
          required
        />

        <Input
          label="SWIFT Code (International)"
          type="text"
          placeholder="Enter SWIFT code"
          value={formData?.swiftCode}
          onChange={(e) => handleInputChange('swiftCode', e?.target?.value)}
          description="Required for international bank accounts"
        />
      </div>

      {/* Document Upload Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Upload" size={20} />
          <span>Required Documentation</span>
        </h3>

        <div className="space-y-4">
          {requiredDocuments?.map(doc => {
            const status = uploadStatus?.[doc?.key];
            const isUploaded = formData?.documents?.[doc?.key];
            const hasError = errors?.[`document_${doc?.key}`];
            
            if (!doc?.required && !isBusinessAgent) {
              return null;
            }

            return (
              <div key={doc?.key} className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-foreground">{doc?.name}</h4>
                      {doc?.required && (
                        <span className="text-xs bg-error/10 text-error px-2 py-1 rounded">Required</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{doc?.description}</p>
                    
                    {isUploaded && (
                      <div className="flex items-center space-x-2 mt-2 text-sm text-success">
                        <Icon name="CheckCircle" size={16} />
                        <span>Document uploaded successfully</span>
                      </div>
                    )}
                    
                    {hasError && (
                      <p className="text-sm text-error mt-2">{hasError}</p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileUpload(doc?.key, e?.target?.files?.[0])}
                      className="hidden"
                      id={`file-${doc?.key}`}
                    />
                    <Button
                      type="button"
                      variant={isUploaded ? "outline" : "default"}
                      size="sm"
                      onClick={() => document.getElementById(`file-${doc?.key}`)?.click()}
                      iconName={getUploadButtonIcon(status)}
                      iconPosition="left"
                      loading={status === 'uploading'}
                      disabled={status === 'uploading'}
                    >
                      {getUploadButtonText(status)}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">Document Requirements</p>
              <ul className="text-blue-700 mt-1 space-y-1">
                <li>• All documents must be clear and legible</li>
                <li>• Accepted formats: PDF, JPG, PNG (max 10MB per file)</li>
                <li>• Documents should be recent (within last 6 months for statements)</li>
                <li>• Business documents must match the registered business name</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-6">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="sm:w-auto"
        >
          Back to Agent Information
        </Button>
        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isLoading}
          iconName="ArrowRight"
          iconPosition="right"
          className="sm:flex-1"
        >
          Continue to Territory Selection
        </Button>
      </div>
    </form>
  );
};

export default BusinessVerificationPanel;